import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import config from "appConfig";
import styles from "../styles/PromotionPackages.scss";
import PromoteButton from "./PromoteButton";
import CancelButton from "./CancelButton";
import history from "../../../history";
import { checkEmptyArray, checkEmptyObject, notification } from "../../../services";
import { HashTag, LocalStorageKey, PageRoute, PaymentMethod, PaymentMethodText, PaymentMethodTextInterface, START_PAGE_PARAM, ShoppingCart, ShoppingCartItemType, ShoppingCartResponse, ShoppingCartStatus } from "../../../models";
import { Checkbox, Form, Radio } from "antd";
import { requiredField } from "../../../models/formValidator";
import { useDispatch, useSelector } from "react-redux";
import { getPromotionPackages, selectPromotionPackages } from "../../../routes/Promotion/promotion.ducks";
import { PromotionPackageInterface, UserI } from "../../../typings/interfaces";
import { mapCategoryToHr, mapModuleToHr } from "../../../routes/LanguageMapper";
import { createCart } from "../../../routes/Cart/cart.ducks";
import { getUserProfile } from "../../../routes/Auth/auth.ducks";
import confirmModal from "../../../services/confirmModal";
import BankTransfer from "../../../components/BankTransfer";
import { redirectByPost } from "../../../utils/RedirectByPost";
import { disableScrollRestoration, restoreScrollRestoration } from "../../../utils/DisableScrollRestoration";
import { getImageUrl } from "../../../services/asset";
import { enableAdAutoPromotion } from "../../../routes/Ad/ad.ducks";
import { StaticHTML } from "../../../components";
import { promotionMultiplier } from "../../../utils/promotionMultiplier";

export interface IPromotionPackagesProps {
  user: UserI;
  adData: any;
  settings: any;
  form: any;
  promotionPackages: PromotionPackageInterface[];
  paymentMethods: PaymentMethodTextInterface[];
}

const defaultSettings = {
  formValidator: {
    promotionPackage: [requiredField(null, "Molimo odaberite jednu od opcija kako biste nastavili dalje.")],
    paymentMethod: [requiredField(null, "Molimo odaberite jednu od opcija kako biste nastavili dalje.")],
  }
};

const PromotionPackages = (props: IPromotionPackagesProps) => {
  const {
    user,
    adData,
    settings: { formValidator },
    form,
    paymentMethods,
  } = props;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const promotionPackages = useSelector(selectPromotionPackages);
  const hideAutoPromoteModal = useRef(false);
  const [showCancelButton, setShowCancelButton] = useState(false);
  const [title, setTitle] = useState(null);
  const [hash, setHash] = useState(null);
  // Read once on mount, before the effect below strips the query from the URL
  const [startPage] = useState(() => new URLSearchParams(history.location.search).get(START_PAGE_PARAM));


  useEffect(() => {
    const adUpdated = history.location.hash === HashTag.adUpdated;
    const adNew = history.location.hash === HashTag.adNew;

    if (adUpdated || adNew) {
      setShowCancelButton(true);
    }

    if (adUpdated) {
      setHash(HashTag.adUpdated);
    }

    if (adNew) {
      setHash(HashTag.adNew);
    }

    if (adUpdated || adNew) history.replace(history.location.pathname);

    // Disable scroll restoration on Chrome and other modern browsers
    disableScrollRestoration();

    //retun scroll restoration when component unmounts
    return () => {
      restoreScrollRestoration();
    }

  }, []);

  useEffect(() => {
    if (!adData) {
      return;
    }

    const categoryMapped = mapCategoryToHr(adData.category, adData.module)
    setTitle(
      promotionMultiplier[categoryMapped] ?
        `Istakni svoj oglas i ostvari do ${promotionMultiplier[categoryMapped]} puta više pregleda` :
        "Istakni svoj oglas u vrh pretrage i ostvari više pregleda")

    if (hash) {
      setTitle(prev => (hash === HashTag.adUpdated ? "Oglas je ažuriran! {0}" : "Oglas je objavljen! {0}").replaceAll("{0}", "<br />" + prev));
    }
  }
    , [adData, hash]);

  useEffect(() => {
    if (adData && adData.module) {
      dispatch(getPromotionPackages(adData.module));
    }
  }, [adData]);

  // Handle submit
  const handleSubmit = useCallback(e => {
    e.preventDefault();

    form.validateFields((error, values) => {

      if (!error) {

        const data: ShoppingCart = {
          item: {
            itemId: values.promotionPackage,
            adId: adData.id,
            type: ShoppingCartItemType.Promotion,
          },
          paymentMethod: values.paymentMethod,
          startPage: startPage ? Number(startPage) : undefined
        };

        setIsSubmitting(true);

        createCart(data, (cart: ShoppingCartResponse) => {
          setIsSubmitting(false);

          // If cart is not created
          if (!cart) {
            return;
          }

          // If cart status is failed
          if (cart.status == ShoppingCartStatus.failed) {
            notification.show("Isticanje oglasa neuspješno");
          } else {

            // If payment method is bank transfer
            if (
              cart.paymentMethod == PaymentMethod.bankTransfer &&
              Object.values(ShoppingCartItemType).includes(cart.type) &&
              cart.bankTransferInfo &&
              checkEmptyObject(cart.bankTransferInfo)
            ) {
              notification.show("Isticanje oglasa čeka plaćanje");

              // open modal with bank transfer instructions , than on OK button click redirect to the details page
              confirmModal.show({
                title: "Podaci za uplatu",
                iconType: "NaN",
                cancelButtonProps: { style: { display: 'none' } }, // hide cancel button
                width: 610,
                wrapClassName: "bankTransferModal",
                bodyStyle: { padding: '30px 20px 20px' },
                onOk() {
                  history.replace(`/${mapModuleToHr(adData.module)}/${mapCategoryToHr(adData.category, adData.module)}${PageRoute.details}/${adData.smartLink}/${adData.code}${HashTag.adUpdated}`);
                },
                onCancel() {
                  history.replace(`/${mapModuleToHr(adData.module)}/${mapCategoryToHr(adData.category, adData.module)}${PageRoute.details}/${adData.smartLink}/${adData.code}${HashTag.adUpdated}`);
                },

                children: <BankTransfer cart={cart} />
              });
            }

            // If payment method is credit card 
            if (
              cart.paymentMethod == PaymentMethod.creditCard &&
              cart.creditCardInfo &&
              cart.creditCardInfo.merchantUrl &&
              cart.creditCardInfo.redirectParameters &&
              checkEmptyObject(cart.creditCardInfo.redirectParameters)
            ) {

              // create post request to the merchant url
              redirectByPost(cart.creditCardInfo.merchantUrl, cart.creditCardInfo.redirectParameters, false);
            }

            // If payment method is store credit and cart status is completed
            if (
              cart.paymentMethod == PaymentMethod.storeCredit &&
              cart.status == ShoppingCartStatus.completed
            ) {
              dispatch(getUserProfile());
              notification.show("Oglas je uspješno istaknut");

              // get all local storage user objects
              const userObjects = JSON.parse(localStorage.getItem(LocalStorageKey.disableAutoPromotionModal) || "[]");

              // get current user object from local storage
              const currentUserObject = userObjects.find(o => o.userId === user.userId) || {};

              // check if should show auto promotion modal 
              const isAutoPromotionModalDisabled =
                (
                  // check if local storage object is not empty and user is not empty and local storage user id is equal to user id and timestamp is greater than now
                  checkEmptyObject(currentUserObject) &&
                  checkEmptyObject(user) &&
                  currentUserObject.userId === user.userId &&
                  currentUserObject.timestamp > new Date().getTime()
                ) || (
                  // check if user is not empty and promotion packages are not empty and user store credit balance is less than the price of the selected promotion package
                  checkEmptyArray(promotionPackages) &&
                  checkEmptyObject(user) &&
                  user.storeCreditBalance < promotionPackages.find(p => p.id == values.promotionPackage).price
                );

              // open modal with auto promotion option
              if (!isAutoPromotionModalDisabled) {

                // remove current user from local storage flag if exists
                checkEmptyArray(userObjects) && localStorage.setItem(
                  LocalStorageKey.disableAutoPromotionModal,
                  JSON.stringify(
                    userObjects.filter(o => o.userId !== user.userId)
                  )
                );

                // show confirm modal
                confirmModal.show({
                  title: "Automatsko reisticanje oglasa",
                  iconType: "NaN",
                  width: 610,
                  wrapClassName: "autoPromoteModal",
                  bodyStyle: { padding: '30px 20px 20px' },
                  okText: "Aktiviraj",
                  onOk() {
                    // enable auto promotion and redirect to the details page
                    enableAdAutoPromotion(adData.id, null, (success) => {
                      success && notification.show("Automatsko reisticanje oglasa je aktivirano");
                      history.push(`/${mapModuleToHr(adData.module)}/${mapCategoryToHr(adData.category, adData.module)}${PageRoute.details}/${adData.smartLink}/${adData.code}${HashTag.adUpdated}`);
                    });
                  },
                  cancelText: "Ne želim",
                  onCancel() {
                    // check if checkbox is checked
                    if (hideAutoPromoteModal.current) {
                      // add current user to local storage flag with timestamp for 15 days
                      userObjects.push({
                        userId: user.userId,
                        timestamp: new Date().setDate(new Date().getDate() + 15)
                      });

                      localStorage.setItem(
                        LocalStorageKey.disableAutoPromotionModal,
                        JSON.stringify(userObjects)
                      );
                    }

                    history.push(`/${mapModuleToHr(adData.module)}/${mapCategoryToHr(adData.category, adData.module)}${PageRoute.details}/${adData.smartLink}/${adData.code}${HashTag.adUpdated}`);
                  },
                  children: <div>
                    <StaticHTML content={"Želiš da tvoj oglas uvijek bude na vrhu pretrage i ostvari maksimalnu vidljivost?{0}{0} Aktiviraj automatsko reisticanje, i tvoj će se oglas, nakon isteka promocije, automatski podizati na vrh istaknutih oglasa sve dok ne isključiš ovu opciju ili dok ne ostaneš bez kredita.".replaceAll("{0}", "<br />")} />
                    <br /><br />
                    <Checkbox
                      className={styles.autoPromoteCheckbox}
                      defaultChecked={hideAutoPromoteModal.current}
                      onChange={(e) => {
                        hideAutoPromoteModal.current = e.target.checked;
                      }}
                    >
                      {"Ne prikazuj ovu poruku narednih 15 dana"}
                    </Checkbox>
                  </div>
                });
              } else {
                history.push(`/${mapModuleToHr(adData.module)}/${mapCategoryToHr(adData.category, adData.module)}${PageRoute.details}/${adData.smartLink}/${adData.code}${HashTag.adUpdated}`);
              }
            }
          }
        });
      }
    });
  }, [form, adData, user, promotionPackages, startPage]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    history.push(`/${mapModuleToHr(adData.module)}/${mapCategoryToHr(adData.category, adData.module)}${PageRoute.details}/${adData.smartLink}/${adData.code}${HashTag.adUpdated}`);
  }, [history, adData]);

  // Check store credit balance
  const insufficientStoreCredits = useMemo(() => {
    const selectedPackage = promotionPackages.find(p => p.id == form.getFieldValue("promotionPackage"));

    if (!selectedPackage || !user) return true;

    return user.storeCreditBalance <= 0 || user.storeCreditBalance < selectedPackage.price;
  }, [user, promotionPackages, form]);

  // Check if store credit balance is less than the minimum package price in current module promotion packages
  const insufficientStoreCreditsForMinPackagePrice = useMemo(() => {
    if (!promotionPackages || !user) return true;

    return user.storeCreditBalance <= 0 || user.storeCreditBalance < Math.min(...promotionPackages.map(p => p.price));
  }, [promotionPackages]);


  return (
    <div className={styles.content}>
      <div className={styles.title}>
        {title && <StaticHTML content={title} />}
      </div>
      <div className={styles.actions}>
        <Form layout="vertical">
          <div className={styles.section}>
            <p className={`${styles.subtitle} ${styles.subtitleFirst}`}>{"Primjer istaknutog oglasa"}</p>
            <div className={styles.example}>
              <div className={styles.imageWrapper}>
                <img className={styles.exampleImage}
                  src={getImageUrl('promotion-showcase.png')}
                  alt={"Primjer istaknutog oglasa"}
                />
              </div>
              <div className={styles.exampleDescription}>
                <p className={styles.exampleTitle}>{"Prednosti istaknutog oglasa:"}</p>
                <ul className={styles.exampleList}>
                  <li className={styles.exampleListItem}>{"Istaknuti oglas se pomiče na vrh rezultata pretrage"}</li>
                  <li className={styles.exampleListItem}>{"Imaju veći broj pregleda oglasa"}</li>
                  <li className={styles.exampleListItem}>{"Vizualno su istaknuti u odnosu na druge oglase"}</li>
                  <li className={styles.exampleListItem}>{"Isticanjem se oglas obnavlja na 30 dana"}</li>
                </ul>
              </div>
            </div>
          </div>

          {checkEmptyArray(promotionPackages) &&
            <div className={styles.section}>
              <p className={styles.subtitle}>{"Trajanje isticanja:"}</p>
              <Form.Item className={styles.formOptions}>
                {form.getFieldDecorator("promotionPackage", {
                  rules: formValidator.promotionPackage
                })(<Radio.Group className={styles.promotionPackages}>
                  <div className={styles.tiles}>
                    {promotionPackages.map(option => (
                      <div
                        className={styles.tileContainer}
                        key={option.id}
                      >
                        <Radio
                          value={option.id}
                          className={`${styles.tile} ${form.getFieldValue("promotionPackage") === option.id ? styles.selected : ""}`}
                        >
                          <div className={styles.title}>{option.name}</div>
                          <p className={styles.saleInfo}>{option.description}</p>
                          <div className={styles.price}>
                            {/* check if storeCredit is checked and if price is 1 for singularity purposes */}
                            {`${option.price} ${(
                              form.getFieldValue("paymentMethod") === PaymentMethod.storeCredit && !insufficientStoreCreditsForMinPackagePrice) ||
                              form.getFieldValue("paymentMethod") === undefined && !insufficientStoreCreditsForMinPackagePrice ?
                              option.price === 1 ?
                                "kredit" :
                                "kredita" :
                              config.currencySymbol} 
                            `}
                          </div>
                        </Radio>
                      </div>
                    ))}
                  </div>
                </Radio.Group>)}
              </Form.Item>
            </div>
          }

          {form.getFieldValue("promotionPackage") && <React.Fragment>
            {checkEmptyArray(paymentMethods) &&
              <div className={styles.section}>
                <p className={styles.subtitle}>{"Opcije plaćanja:"}</p>
                <Form.Item className={styles.formOptions}>
                  {form.getFieldDecorator("paymentMethod", {
                    rules: formValidator.paymentMethod,
                    initialValue: insufficientStoreCredits ? undefined : PaymentMethod.storeCredit
                  })(<Radio.Group className={styles.paymentMethods}>
                    {paymentMethods &&
                      paymentMethods.map((option, index) => (
                        <React.Fragment key={index}>
                          <Radio
                            value={option.value}
                            className={`${styles.item} 
                            ${option.value == PaymentMethod.creditCard && form.getFieldValue("paymentMethod") == PaymentMethod.creditCard ?
                                styles.creditCardFocued : ""}`}
                            key={option.value}
                            disabled={option.value == PaymentMethod.storeCredit && insufficientStoreCredits}
                          >
                            <div className={styles.tile}>
                              <span className={styles.title}>{option.name}</span>
                              <span className={styles.desc}>
                                <span>({option.description})</span>

                                {option.value == PaymentMethod.storeCredit && user ? (
                                  <span>- {option.state.replace("{0}", user.storeCreditBalance.toString())}</span>
                                ) : null}

                                {option.value == PaymentMethod.storeCredit ? (
                                  <span className={styles.storeCredit}>
                                    <a onClick={() => history.push({
                                      pathname: PageRoute.credits,
                                      state: { redirectAfterCreditPurchase: history.location.pathname }
                                    })}>
                                      {"Kupi kredite"}
                                    </a>
                                  </span>
                                ) : null}

                              </span>
                            </div>
                          </Radio>

                          {
                            //check if credit card radio is selected 
                            option.value == PaymentMethod.creditCard && form.getFieldValue("paymentMethod") == PaymentMethod.creditCard &&
                            <div className={styles.creditCardOption}>
                              <img className={styles.creditCardImg} src={getImageUrl("corvus-logo.png")} alt="CorvusPay" />
                              <p className={styles.creditCardTxt}>{"Pri plaćanju na našoj web trgovini koristite CorvusPay – napredni sustav za siguran prihvat platnih kartica putem interneta. CorvusPay sustav osigurava potpunu tajnost Vaših kartičnih i osobnih podataka već od trenutka kada ih upišete u CorvusPay platni formular. Platni podaci prosljeđuju se šifrirano od Vašeg web preglednika do banke koja je izdala Vašu karticu. Naša trgovina nikada ne dolazi u kontakt s cjelovitim podacima o Vašoj platnoj kartici. Također, podaci su nedostupni čak i djelatnicima CorvusPay sustava. Izolirana jezgra samostalno prenosi i upravlja osjetljivim podacima, čuvajući ih pri tom potpuno sigurnima."}</p>
                            </div>
                          }
                        </React.Fragment>

                      ))}
                  </Radio.Group>)}
                </Form.Item>
              </div>
            }
          </React.Fragment>}
        </Form>

      </div>
      <div className={styles.actionButtons}>
        {showCancelButton && (
          <CancelButton
            onClick={handleCancel}
          />
        )}
        <PromoteButton
          onClick={handleSubmit}
          isEnable={true}
          isLoading={isSubmitting}
        />
      </div>
    </div>
  );
};

PromotionPackages.defaultProps = {
  settings: defaultSettings,
  promotionPackages: [],
  paymentMethods: PaymentMethodText
};

export default Form.create()(PromotionPackages);



// WEBPACK FOOTER //
// ./src/containers/AdPromotePage/PromotionPackages/index.tsx